import { persistor, store, clearAuth, baseApi } from "../store";
import { redirectToSsoLogin } from "./ssoConfig";

/**
 * Expire every document.cookie the page can see (HttpOnly cookies cannot be removed from JS).
 */
function clearDocumentCookies() {
  if (typeof document === "undefined") {
    return;
  }

  const hostname = window.location.hostname;
  const domainCandidates = ["", hostname, `.${hostname}`];
  const pathCandidates = ["/", window.location.pathname || "/"];

  for (const raw of document.cookie.split(";")) {
    const name = raw.split("=")[0]?.trim();
    if (!name) {
      continue;
    }
    for (const domain of domainCandidates) {
      for (const path of pathCandidates) {
        let cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}`;
        if (domain) {
          cookie += `;domain=${domain}`;
        }
        document.cookie = cookie;
      }
    }
  }
}

async function clearCacheStorage() {
  if (typeof window === "undefined" || !window.caches?.keys) {
    return;
  }
  const keys = await window.caches.keys();
  await Promise.all(keys.map((key) => window.caches.delete(key)));
}

async function unregisterServiceWorkers() {
  if (typeof navigator === "undefined" || !navigator.serviceWorker?.getRegistrations) {
    return;
  }
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
}

/**
 * Clears Redux (incl. persist), same-origin storage, Cache Storage, and document cookies.
 * Browser HTTP cache and third-party / HttpOnly cookies cannot be cleared from the page.
 */
export async function clearSiteCacheAndCookies() {
  if (typeof window === "undefined") {
    return;
  }

  try {
    store.dispatch(clearAuth());
    store.dispatch(baseApi.util.resetApiState());
  } catch {
    // Store may be unavailable during teardown.
  }

  try {
    persistor.pause();
    await persistor.purge();
    await persistor.flush();
  } catch {
    // Persist is best-effort; storage wipe below still runs.
  }

  try {
    window.sessionStorage.clear();
  } catch {
    // Ignore quota / security errors.
  }

  try {
    window.localStorage.clear();
  } catch {
    // Ignore quota / security errors.
  }

  clearDocumentCookies();

  try {
    await clearCacheStorage();
  } catch {
    // Cache Storage may be blocked.
  }

  try {
    await unregisterServiceWorkers();
  } catch {
    // Service workers are optional in this app.
  }
}

/**
 * Retry SSO: wipe site session data, then go straight to the
 * environment start-SSO URL (no intermediate “in progress” page).
 */
export async function retrySsoLogin() {
  await clearSiteCacheAndCookies();
  if (!redirectToSsoLogin() && typeof window !== "undefined") {
    window.location.replace("/");
  }
}
